import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  BarChart3, TrendingUp, TrendingDown, Users, GraduationCap, 
  Calendar, DollarSign, Clock, Target, Activity, Building2,
  ChevronUp, ChevronDown, Layers
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GestionDashboard({ academiaId, sucursales = [], categorias = [] }) {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('clases');
  const [estadisticas, setEstadisticas] = useState(null);
  const [finanzas, setFinanzas] = useState(null);
  
  // Filtros
  const [filtroSucursal, setFiltroSucursal] = useState('todas');
  const [filtroCategoria, setFiltroCategoria] = useState('todas');
  const [fechaInicio, setFechaInicio] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [fechaFin, setFechaFin] = useState(() => new Date().toISOString().split('T')[0]);

  useEffect(() => {
    if (academiaId) loadData();
  }, [academiaId, filtroSucursal, filtroCategoria, fechaInicio, fechaFin]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtroSucursal !== 'todas') params.append('sucursal_id', filtroSucursal);
      if (filtroCategoria !== 'todas') params.append('categoria_id', filtroCategoria);
      if (fechaInicio) params.append('fecha_inicio', fechaInicio);
      if (fechaFin) params.append('fecha_fin', fechaFin);
      
      const [estRes, finRes] = await Promise.all([
        axios.get(`${API}/academias/${academiaId}/estadisticas/resumen?${params}`, { withCredentials: true }),
        axios.get(`${API}/academias/${academiaId}/estadisticas/finanzas?${params}`, { withCredentials: true })
      ]);
      
      setEstadisticas(estRes.data);
      setFinanzas(finRes.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  };

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(amount || 0);
  };

  // Categorías filtradas por sucursal
  const categoriasFiltradas = filtroSucursal === 'todas' 
    ? categorias 
    : categorias.filter(c => c.sucursal_id === filtroSucursal);

  // Simple bar chart component
  const SimpleBarChart = ({ data, valueKey, labelKey, color = 'emerald' }) => {
    if (!data || data.length === 0) return <p className="text-slate-500 text-sm text-center py-4">Sin datos</p>;
    
    const maxValue = Math.max(...data.map(d => d[valueKey] || 0));
    
    return (
      <div className="space-y-2">
        {data.map((item, idx) => (
          <div key={idx} className="flex items-center gap-3">
            <div className="w-24 text-xs text-slate-400 truncate">{item[labelKey]}</div>
            <div className="flex-1 bg-slate-700 rounded-full h-4 overflow-hidden">
              <div 
                className={`h-full bg-${color}-500 rounded-full transition-all duration-500`}
                style={{ width: `${maxValue > 0 ? (item[valueKey] / maxValue * 100) : 0}%` }}
              />
            </div>
            <div className="w-12 text-right text-sm text-white font-medium">{item[valueKey]}</div>
          </div>
        ))}
      </div>
    );
  };

  // Tendencia chart
  const TrendChart = ({ data }) => {
    if (!data || data.length === 0) return <p className="text-slate-500 text-sm text-center py-4">Sin datos</p>;
    
    const maxVal = Math.max(...data.map(d => d.porcentaje || 0));
    const minVal = Math.min(...data.map(d => d.porcentaje || 0));
    const range = maxVal - minVal || 1;
    
    return (
      <div className="relative h-32">
        <div className="absolute inset-0 flex items-end justify-between gap-1 px-2">
          {data.map((item, idx) => (
            <div key={idx} className="flex-1 flex flex-col items-center gap-1">
              <div 
                className="w-full bg-emerald-500 rounded-t transition-all duration-500"
                style={{ height: `${Math.max(10, (item.porcentaje - minVal) / range * 100)}%` }}
              />
              <span className="text-xs text-slate-500">{item.semana}</span>
            </div>
          ))}
        </div>
        {/* Labels */}
        <div className="absolute left-0 top-0 text-xs text-slate-500">{Math.round(maxVal)}%</div>
        <div className="absolute left-0 bottom-6 text-xs text-slate-500">{Math.round(minVal)}%</div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header con filtros */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard de Gestión</h1>
          <p className="text-slate-400 text-sm">Estadísticas y métricas de la academia</p>
        </div>
      </div>

      {/* Filtros */}
      <Card className="p-4 bg-slate-800 border-slate-700">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label className="text-slate-400 text-xs">Sucursal</Label>
            <Select value={filtroSucursal} onValueChange={setFiltroSucursal}>
              <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas las sucursales</SelectItem>
                {sucursales.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-400 text-xs">Categoría</Label>
            <Select value={filtroCategoria} onValueChange={setFiltroCategoria}>
              <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas las categorías</SelectItem>
                {categoriasFiltradas.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-400 text-xs">Desde</Label>
            <Input
              type="date"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              className="bg-slate-700 border-slate-600 text-white"
            />
          </div>
          <div>
            <Label className="text-slate-400 text-xs">Hasta</Label>
            <Input
              type="date"
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
              className="bg-slate-700 border-slate-600 text-white"
            />
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <div className="flex gap-2">
        <Button 
          variant={activeTab === 'clases' ? 'default' : 'outline'}
          onClick={() => setActiveTab('clases')}
          className={activeTab === 'clases' ? 'bg-emerald-600' : 'border-slate-600'}
        >
          <Activity className="w-4 h-4 mr-2" /> Clases y Asistencia
        </Button>
        <Button 
          variant={activeTab === 'finanzas' ? 'default' : 'outline'}
          onClick={() => setActiveTab('finanzas')}
          className={activeTab === 'finanzas' ? 'bg-emerald-600' : 'border-slate-600'}
        >
          <DollarSign className="w-4 h-4 mr-2" /> Finanzas
        </Button>
      </div>

      {/* Tab: Clases y Asistencia */}
      {activeTab === 'clases' && estadisticas && (
        <div className="space-y-6">
          {/* KPIs principales */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="p-4 bg-slate-800 border-slate-700">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-500/20 rounded-lg">
                  <Calendar className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-xs text-slate-400">Clases Realizadas</p>
                  <p className="text-2xl font-bold text-white">{estadisticas.clases?.total_realizadas || 0}</p>
                </div>
              </div>
            </Card>
            
            <Card className="p-4 bg-slate-800 border-slate-700">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <Target className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-xs text-slate-400">Asistencia Promedio</p>
                  <p className="text-2xl font-bold text-white">{estadisticas.clases?.asistencia_promedio || 0}%</p>
                </div>
              </div>
            </Card>
            
            <Card className="p-4 bg-slate-800 border-slate-700">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <Users className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-xs text-slate-400">Alumnos Activos</p>
                  <p className="text-2xl font-bold text-white">{estadisticas.totales?.alumnos_activos || 0}</p>
                </div>
              </div>
            </Card>
            
            <Card className="p-4 bg-slate-800 border-slate-700">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-500/20 rounded-lg">
                  <GraduationCap className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-xs text-slate-400">Entrenadores</p>
                  <p className="text-2xl font-bold text-white">{estadisticas.totales?.entrenadores_activos || 0}</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Gráficos */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Tendencia de asistencia */}
            <Card className="p-4 bg-slate-800 border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-400" />
                Tendencia de Asistencia
              </h3>
              <TrendChart data={estadisticas.tendencia_asistencia} />
              <p className="text-xs text-slate-500 text-center mt-2">Últimas 8 semanas</p>
            </Card>

            {/* Clases por entrenador */}
            <Card className="p-4 bg-slate-800 border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-400" />
                Clases por Entrenador
              </h3>
              <SimpleBarChart 
                data={estadisticas.por_entrenador?.slice(0, 5)} 
                valueKey="total" 
                labelKey="entrenador_nombre"
                color="purple"
              />
            </Card>

            {/* Clases por categoría */}
            <Card className="p-4 bg-slate-800 border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Layers className="w-5 h-5 text-blue-400" />
                Clases por Categoría
              </h3>
              <SimpleBarChart 
                data={estadisticas.por_categoria?.slice(0, 5)} 
                valueKey="total" 
                labelKey="categoria_nombre"
                color="blue"
              />
            </Card>

            {/* Resumen general */}
            <Card className="p-4 bg-slate-800 border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-emerald-400" />
                Resumen de Asistencia
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Total Asistencias</span>
                  <span className="text-white font-semibold">{estadisticas.clases?.total_asistencias || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Posibles (capacidad)</span>
                  <span className="text-white font-semibold">{estadisticas.clases?.total_posibles || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Inasistencias</span>
                  <span className="text-red-400 font-semibold">
                    {(estadisticas.clases?.total_posibles || 0) - (estadisticas.clases?.total_asistencias || 0)}
                  </span>
                </div>
                <div className="pt-3 border-t border-slate-700">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-300 font-medium">Tasa de Asistencia</span>
                    <span className={`text-lg font-bold ${
                      (estadisticas.clases?.asistencia_promedio || 0) >= 80 ? 'text-emerald-400' :
                      (estadisticas.clases?.asistencia_promedio || 0) >= 60 ? 'text-yellow-400' : 'text-red-400'
                    }`}>
                      {estadisticas.clases?.asistencia_promedio || 0}%
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Tab: Finanzas */}
      {activeTab === 'finanzas' && finanzas && (
        <div className="space-y-6">
          {/* KPIs financieros */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="p-4 bg-slate-800 border-slate-700">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-500/20 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-xs text-slate-400">Ingresos Período</p>
                  <p className="text-xl font-bold text-emerald-400">{formatMoney(finanzas.contabilidad?.ingresos_periodo)}</p>
                </div>
              </div>
            </Card>
            
            <Card className="p-4 bg-slate-800 border-slate-700">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-500/20 rounded-lg">
                  <TrendingDown className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <p className="text-xs text-slate-400">Egresos Período</p>
                  <p className="text-xl font-bold text-red-400">{formatMoney(finanzas.contabilidad?.egresos_periodo)}</p>
                </div>
              </div>
            </Card>
            
            <Card className="p-4 bg-slate-800 border-slate-700">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${(finanzas.contabilidad?.balance_periodo || 0) >= 0 ? 'bg-emerald-500/20' : 'bg-red-500/20'}`}>
                  {(finanzas.contabilidad?.balance_periodo || 0) >= 0 ? 
                    <ChevronUp className="w-5 h-5 text-emerald-400" /> : 
                    <ChevronDown className="w-5 h-5 text-red-400" />
                  }
                </div>
                <div>
                  <p className="text-xs text-slate-400">Balance Período</p>
                  <p className={`text-xl font-bold ${(finanzas.contabilidad?.balance_periodo || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {formatMoney(finanzas.contabilidad?.balance_periodo)}
                  </p>
                </div>
              </div>
            </Card>
            
            <Card className="p-4 bg-slate-800 border-slate-700">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-500/20 rounded-lg">
                  <Clock className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-xs text-slate-400">Por Pagar</p>
                  <p className="text-xl font-bold text-amber-400">{formatMoney(finanzas.cuentas_por_pagar?.monto_total)}</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Detalle de cuotas */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="p-4 bg-slate-800 border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-emerald-400" />
                Estado de Cuotas
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-emerald-500/10 rounded-lg">
                  <span className="text-emerald-300">Total Cuotas Pagadas</span>
                  <span className="text-emerald-400 font-bold">{formatMoney(finanzas.cuotas?.total_pagadas)}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-red-500/10 rounded-lg">
                  <span className="text-red-300">Total Deuda Pendiente</span>
                  <span className="text-red-400 font-bold">{formatMoney(finanzas.cuotas?.total_deuda)}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-slate-700 rounded-lg">
                  <span className="text-slate-300">Alumnos con Deuda</span>
                  <Badge className="bg-red-500/20 text-red-400">{finanzas.cuotas?.alumnos_con_deuda || 0}</Badge>
                </div>
              </div>
            </Card>

            <Card className="p-4 bg-slate-800 border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-400" />
                Cuentas por Pagar
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-slate-700 rounded-lg">
                  <span className="text-slate-300">Cantidad de Cuentas</span>
                  <Badge className="bg-amber-500/20 text-amber-400">{finanzas.cuentas_por_pagar?.cantidad || 0}</Badge>
                </div>
                <div className="flex justify-between items-center p-3 bg-amber-500/10 rounded-lg">
                  <span className="text-amber-300">Monto Total Pendiente</span>
                  <span className="text-amber-400 font-bold">{formatMoney(finanzas.cuentas_por_pagar?.monto_total)}</span>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
